import React, { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";
import { buildRecordSections, countSectionFields } from "utilities/recordSchema";
import {
  canEditSectionField,
  isRowEditableSection,
  describeSaveError,
} from "utilities/fieldEdit";
import { withInternalOwnerFields } from "utilities/ownerTitle";
import RecordFieldRow from "components/Registration/RecordFieldRow";

/**
 * SOUND-643 Phase 1 — the schema-driven record view.
 *
 * Renders EVERY field the schema declares, grouped only where the schema already
 * groups them. Replaces the hand-written <InfoField> list that had drifted to 21
 * of 55 scalar fields and hid `riskAssessment` entirely — the merchant's own
 * disclosures about terminations, PCI compliance and Visa risk programs.
 *
 * All decisions live in utilities/recordSchema.js, which has tests. This file
 * maps over the result and nothing more, because sound-app has no component-test
 * harness and anything expressed here is unverifiable.
 *
 * DESIGN
 * - Sections are collapsible, expanded by default. Collapsing is a reading aid,
 *   never a way to hide data that would otherwise be unreachable.
 * - Blank fields render as an em-dash. A blank row is how a reader discovers the
 *   field exists at all; omitting it is what let `effectiveDate` sit null on 261
 *   MIDs unnoticed.
 * - Single column of full-width sections. The old two-thirds/one-third split put
 *   Processing in a narrow rail, which is survivable at 5 fields and not at 26.
 * - Repeatables (owners, DBAs) render inline as field grids, one block per item.
 */

function GroupBlock({ group, sectionKey, rowId, canEdit, onSave }) {
  const isRisk = group.variant === "risk";
  return (
    <div className={isRisk ? "rounded-lg ring-1 ring-warning/30 bg-warning/5 p-4" : undefined}>
      {group.title && (
        <h4
          className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide mb-3 ${
            isRisk ? "text-warning" : "text-muted-foreground"
          }`}
        >
          {isRisk && <AlertTriangle className="h-3.5 w-3.5" />}
          {group.title}
        </h4>
      )}
      <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
        {group.fields.map((f) => (
          <div
            key={f.name}
            // `gridCols` is the COLUMN COUNT the field wants (1 = full width),
            // not a span. Only full-width fields and textareas need to break out.
            className={
              f.gridCols === 1 || f.type === "textarea"
                ? "md:col-span-2 lg:col-span-3"
                : undefined
            }
          >
            <RecordFieldRow
              field={f}
              sectionKey={sectionKey}
              rowId={rowId}
              canEdit={canEdit}
              onSave={onSave}
            />
          </div>
        ))}
      </dl>
    </div>
  );
}

function Section({ section, canEdit, onSave, onAddRow, onRemoveRow, busyRowId }) {
  const [open, setOpen] = useState(true);
  const { total, empty } = countSectionFields(section);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="surface rounded-xl ring-1 ring-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h3 className="text-base font-semibold text-foreground truncate">
            {section.title}
          </h3>
          {section.repeatable && (
            <span className="badge badge-info shrink-0">{section.count}</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground shrink-0 ml-3">
          {total} field{total === 1 ? "" : "s"}
          {empty > 0 && <span className="text-muted-foreground/60"> · {empty} blank</span>}
        </span>
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-6 border-t border-border pt-5">
          {section.description && (
            <p className="text-sm text-muted-foreground -mt-1">{section.description}</p>
          )}

          {section.blocks.length === 0 && (
            <p className="text-sm text-muted-foreground/60">
              No {section.itemLabel ? `${section.itemLabel.toLowerCase()}s` : "entries"} on record.
            </p>
          )}

          {section.blocks.map((block) => (
            <div key={block.rowId || block.index} className="space-y-4">
              {block.label && (
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    {block.label}
                    {section.itemLabel && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {section.itemLabel} {block.index + 1}
                      </span>
                    )}
                  </h4>
                  {/* Removal needs the row's own id. A row with none predates
                      SOUND-649 and cannot be addressed individually, so it is
                      shown without the control rather than with one that would
                      delete by position. */}
                  {onRemoveRow && block.rowId && (
                    <button
                      type="button"
                      className="btn btn-ghost h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                      disabled={busyRowId === block.rowId}
                      onClick={() => onRemoveRow(block)}
                      title={`Remove this ${(section.itemLabel || "row").toLowerCase()}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </button>
                  )}
                </div>
              )}
              <div className="space-y-5">
                {block.groups.map((g, i) => (
                  <GroupBlock
                    key={g.key || `g-${i}`}
                    group={g}
                    sectionKey={section.key}
                    rowId={block.rowId}
                    canEdit={canEdit}
                    onSave={onSave}
                  />
                ))}
              </div>
            </div>
          ))}

          {onAddRow && (
            <button
              type="button"
              className="btn btn-outline w-full"
              onClick={onAddRow}
              disabled={busyRowId === "__adding__"}
            >
              <Plus className="h-4 w-4" />
              {busyRowId === "__adding__"
                ? "Adding…"
                : `Add ${section.itemLabel || "row"}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @param {object}   schema       applicationSchema
 * @param {object}   application  the stored document
 * @param {string[]} only         render only these step keys, in schema order.
 *                                Lets the caller interleave a section that still
 *                                has bespoke behaviour — today the DBAs card,
 *                                which carries the only working inline editor and
 *                                must keep its position until Phase 2 replaces it.
 */
export default function RecordSections({
  schema,
  application,
  only = null,
  applicationId,
  editContext = null, // { isInternal, isPartner, status } — omit for read-only
  onSaved,
}) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();
  // Above the early return — a hook after a conditional return changes hook
  // order between renders.
  const [busyRowId, setBusyRowId] = useState(null);

  let sections = buildRecordSections(schema, application);
  // SOUND-775: internal-only owner job title (required by Kurv). Injected into
  // the render model, NOT the schema, because the schema also drives the
  // merchant wizard. Clients and partners never see the field.
  sections = withInternalOwnerFields(sections, application, {
    isInternal: Boolean(editContext?.isInternal),
  });
  if (only) sections = sections.filter((s) => only.includes(s.key));
  if (sections.length === 0) return null;

  /**
   * Scalar sections PUT the application and are deep-merged. Row sections PUT
   * the ROW — the section PUT would wholesale-replace the array and delete
   * every other owner, which is the bug this replaced.
   */
  const saveField = async ({ payload, sectionKey, rowId, fieldName }) => {
    const rowScoped = isRowEditableSection(sectionKey);
    if (rowScoped && !rowId) {
      toast({
        status: "destructive",
        title: "Cannot save",
        description:
          "This row has no identifier, so it cannot be edited individually.",
      });
      throw new Error("row save attempted without a rowId");
    }
    const url = rowScoped
      ? apiRoutes.updateApplicationOwner(applicationId, rowId)
      : apiRoutes.updateApplication(applicationId);
    try {
      await makeApiRequest("put", url, auth.user, effectiveCompanyId, payload);
      toast({ status: "success", title: "Saved" });
      onSaved?.();
    } catch (err) {
      // Field-level problems come back in details[] (400) or fields[] (403 —
      // an internal-only field such as owners.title); surface them rather than
      // a bare "failed", which sends people to DevTools.
      const { message, forField } = describeSaveError(err.response?.data, fieldName);
      toast({ status: "destructive", title: "Failed to save", description: message });
      // RecordFieldRow shows this under the input. The toast vanishes; the
      // inline message stays until the value changes.
      err.fieldMessage = forField ? message : null;
      throw err; // keeps the editor open so the typed value is not discarded
    }
  };

  const addRow = async (section) => {
    setBusyRowId("__adding__");
    try {
      await makeApiRequest(
        "post",
        apiRoutes.addApplicationOwner(applicationId),
        auth.user,
        effectiveCompanyId,
        // The API requires a name; a placeholder keeps the row addressable
        // immediately so every other field can then be filled in one at a time.
        { fullName: `New ${section.itemLabel || "entry"}` }
      );
      toast({ status: "success", title: `${section.itemLabel || "Row"} added` });
      onSaved?.();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to add",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const removeRow = async (section, block) => {
    if (
      !window.confirm(
        `Remove ${block.label}? This cannot be undone.`
      )
    ) {
      return;
    }
    setBusyRowId(block.rowId);
    try {
      await makeApiRequest(
        "delete",
        apiRoutes.deleteApplicationOwner(applicationId, block.rowId),
        auth.user,
        effectiveCompanyId
      );
      toast({ status: "success", title: `${section.itemLabel || "Row"} removed` });
      onSaved?.();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to remove",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setBusyRowId(null);
    }
  };

  return (
    <div className="space-y-6">
      {sections.map((s) => {
        const canEdit =
          Boolean(editContext) &&
          canEditSectionField({ sectionKey: s.key, ...editContext });
        // Add/remove ride on the same permission as field editing, and only
        // exist for sections with a per-row endpoint behind them.
        const rowControls = canEdit && isRowEditableSection(s.key);
        return (
          <Section
            key={s.key}
            section={s}
            canEdit={canEdit}
            onSave={saveField}
            busyRowId={busyRowId}
            onAddRow={rowControls ? () => addRow(s) : null}
            onRemoveRow={rowControls ? (block) => removeRow(s, block) : null}
          />
        );
      })}
    </div>
  );
}
